/**
 * Sentry SDK initialization for frontend.
 */
import * as Sentry from "@sentry/react";
import type { Metric } from "web-vitals";
import { onCLS, onINP, onLCP, onTTFB } from "web-vitals";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || "dev";

// Sensitive patterns to scrub
const SENSITIVE_PATTERNS = [/password/i, /token/i, /authorization/i, /api[-_]?key/i, /secret/i];

function scrubData(data: Record<string, unknown>): Record<string, unknown> {
  const scrubbed = { ...data };
  for (const key of Object.keys(scrubbed)) {
    if (SENSITIVE_PATTERNS.some((pattern) => pattern.test(key))) {
      scrubbed[key] = "[Filtered]";
    }
  }
  return scrubbed;
}

export function initSentry(): void {
  if (!SENTRY_DSN) {
    // Only log in dev to avoid noise
    if (ENVIRONMENT === "dev") {
      console.log("Sentry: disabled (no DSN configured)");
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: __APP_VERSION__,

    // Performance Monitoring
    tracesSampleRate: ENVIRONMENT === "prod" ? 0.1 : 1.0,

    integrations: [Sentry.browserTracingIntegration()],

    // Filter sensitive data
    beforeSend(event) {
      if (event.request?.data && typeof event.request.data === "object") {
        event.request.data = scrubData(event.request.data as Record<string, unknown>);
      }
      return event;
    },

    // Limit breadcrumbs
    maxBreadcrumbs: 50,

    // Don't send PII
    sendDefaultPii: false,
  });
}

/**
 * Report a Web Vital metric to Sentry.
 */
function reportWebVital(metric: Metric): void {
  // CLS is unitless, others are in milliseconds
  const unit = metric.name === "CLS" ? "" : "millisecond";
  Sentry.setMeasurement(metric.name.toLowerCase(), metric.value, unit);

  // Also add as span attribute for transaction context
  const span = Sentry.getActiveSpan();
  if (span) {
    span.setAttribute(`web_vital.${metric.name.toLowerCase()}`, metric.value);
  }
}

/**
 * Initialize Web Vitals monitoring.
 * Reports Core Web Vitals (LCP, INP, CLS) and additional metrics (TTFB) to Sentry.
 */
export function initWebVitals(): void {
  if (!SENTRY_DSN) {
    // Skip if Sentry is not configured
    return;
  }

  // Core Web Vitals (as of 2024: LCP, INP, CLS)
  onLCP(reportWebVital); // Largest Contentful Paint
  onINP(reportWebVital); // Interaction to Next Paint (replaced FID)
  onCLS(reportWebVital); // Cumulative Layout Shift

  // Additional metrics
  onTTFB(reportWebVital); // Time to First Byte
}

export { Sentry };
